import Client from '../Client.provider';
import {
  iFloorMapData,
  iFloorMapLayer,
  iFloorMapLayerInput,
  iFloorMapSaveResponse,
  iFloorMapVersion,
} from './FloorMap.type';

export interface iFloorMapRepository {
  getFloorMap: (eventId: string) => Promise<iFloorMapData | null>;
  saveFloorMap: (
    eventId: string,
    data: Partial<iFloorMapData>
  ) => Promise<iFloorMapSaveResponse>;
  setOnline: (eventId: string, isOnline: boolean) => Promise<boolean>;
  getVersions: (
    eventId: string,
    forkerId?: string
  ) => Promise<iFloorMapVersion[]>;
  getVersion: (eventId: string, versionId: number) => Promise<iFloorMapData>;
  restoreVersion: (
    eventId: string,
    versionId: number
  ) => Promise<iFloorMapSaveResponse>;
  getLayers: (eventId: string) => Promise<iFloorMapLayer[]>;
  createLayer: (
    eventId: string,
    data: iFloorMapLayerInput
  ) => Promise<iFloorMapLayer>;
  updateLayer: (
    eventId: string,
    layerId: number,
    data: iFloorMapLayerInput
  ) => Promise<iFloorMapLayer>;
  deleteLayer: (eventId: string, layerId: number) => Promise<void>;
}

export class FloorMapRepository extends Client implements iFloorMapRepository {
  public async getFloorMap(eventId: string): Promise<iFloorMapData | null> {
    return await this.restricted()
      .get<iFloorMapData | null>(`/floormap/${eventId}`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknow error...`
        );
      });
  }

  public async saveFloorMap(
    eventId: string,
    data: Partial<iFloorMapData>
  ): Promise<iFloorMapSaveResponse> {
    return await this.restricted()
      .post(`/floormap/${eventId}`, data)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknow error...`
        );
      });
  }

  public async setOnline(eventId: string, isOnline: boolean): Promise<boolean> {
    return await this.restricted()
      .put<{ is_online: boolean }>(`/floormap/${eventId}/online`, {
        is_online: isOnline,
      })
      .then((e) => e.data.is_online)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknow error...`
        );
      });
  }

  public async getVersions(
    eventId: string,
    forkerId?: string
  ): Promise<iFloorMapVersion[]> {
    return await this.restricted()
      .get<iFloorMapVersion[]>(`/floormap/${eventId}/versions`, {
        params: forkerId ? { forker_id: forkerId } : undefined,
      })
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknow error...`
        );
      });
  }

  public async getVersion(
    eventId: string,
    versionId: number
  ): Promise<iFloorMapData> {
    return await this.restricted()
      .get<iFloorMapData>(`/floormap/${eventId}/version/${versionId}`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknow error...`
        );
      });
  }

  public async restoreVersion(
    eventId: string,
    versionId: number
  ): Promise<iFloorMapSaveResponse> {
    return await this.restricted()
      .put(`/floormap/${eventId}/restore/${versionId}`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknow error...`
        );
      });
  }

  public async getLayers(eventId: string): Promise<iFloorMapLayer[]> {
    return await this.restricted()
      .get<iFloorMapLayer[]>(`/floormap/${eventId}/layers`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknow error...`
        );
      });
  }

  public async createLayer(
    eventId: string,
    data: iFloorMapLayerInput
  ): Promise<iFloorMapLayer> {
    return await this.restricted()
      .post<iFloorMapLayer>(`/floormap/${eventId}/layers`, data)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknow error...`
        );
      });
  }

  public async updateLayer(
    eventId: string,
    layerId: number,
    data: iFloorMapLayerInput
  ): Promise<iFloorMapLayer> {
    return await this.restricted()
      .put<iFloorMapLayer>(`/floormap/${eventId}/layers/${layerId}`, data)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknow error...`
        );
      });
  }

  public async deleteLayer(eventId: string, layerId: number): Promise<void> {
    await this.restricted()
      .delete(`/floormap/${eventId}/layers/${layerId}`)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknow error...`
        );
      });
  }
}
